import type { Halo as HaloStyle, Pilot } from '@edh/shared';
import { pilotEloSeries, rankFor, rankThrough } from '@edh/shared';
import { useStore } from '../lib/store';

/**
 * A rank, shown as its halo.
 *
 * The mark carries the rank and the word confirms it, not the other way round —
 * in a list of six people the ring says who is where before a single name is
 * read. Ornament does the work colour cannot: past silver and gold there are no
 * distinguishable hues left, but there is always another ring to add.
 */
export function RankBadge({
  pilot,
  elo,
  atIndex,
  held = false,
  size = 'normal',
}: {
  /**
   * Preferred. Given the member, the badge works out grace for itself.
   *
   * Every call site that passed only a rating got the rank the number alone
   * implies, which is the wrong one for anybody being held below a floor —
   * somebody sitting at Anointed III on 1191 was billed as Mortal I on the
   * setup screen. Deriving it here means a new call site cannot make that
   * mistake by omission.
   */
  pilot?: Pilot;
  elo?: number;
  /** Only for callers that have already worked the standing out. */
  atIndex?: number;
  held?: boolean;
  size?: 'normal' | 'small';
}) {
  const { state } = useStore();
  const derived = pilot
    ? rankThrough(pilotEloSeries(state.matches, pilot.id, pilot.elo))
    : null;
  const rating = pilot?.elo ?? elo ?? 0;
  const rank = rankFor(rating, derived ? derived.index : atIndex);
  const isHeld = derived ? derived.held : held;
  return (
    <span
      className={`rank-badge${size === 'small' ? ' small' : ''}${isHeld ? ' held' : ''}`}
      title={isHeld ? `Below ${rank.name}, held on grace` : undefined}
    >
      <Halo style={rank.halo} color={rank.color} glow={rank.glow && !isHeld} held={isHeld} />
      {/* One item, not two. "Anointed III" is a single thing somebody says out
          loud, and in a place narrow enough for the badge to wrap — a seat in
          the lobby — two items is a rank on one line and its numeral on the
          next. */}
      <span className="rank-badge-title">
        <span className="rank-badge-name" style={{ color: rank.color }}>
          {rank.name}
        </span>
        <span className="rank-badge-level">{'I'.repeat(rank.level)}</span>
      </span>
      {/* Said out loud rather than left to a dimmed ring. Somebody below their
          rank's floor should know it, and know it is not permanent yet. */}
      {isHeld && <span className="rank-held">in grace</span>}
    </span>
  );
}

/**
 * The halo on its own, at whatever size the caller needs.
 *
 * Drawn in a 28-unit box with the ring at radius 9, leaving room outside it for
 * the rays the top two ranks get. Everything scales from that, so a halo in a
 * list row and a halo around a 64px avatar are the same drawing.
 */
export function Halo({
  style,
  color,
  glow = false,
  held = false,
  className,
}: {
  style: HaloStyle;
  color: string;
  glow?: boolean;
  /** Held on grace: the ring breaks rather than fading. */
  held?: boolean;
  className?: string;
}) {
  const C = 14;
  // Broken at full colour, not faded. A faded ring is a paler ring, and a paler
  // ring is what a different rank looks like.
  const ring = (r: number, width: number, dash?: string) => (
    <circle
      cx={C}
      cy={C}
      r={r}
      fill="none"
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
      strokeDasharray={held ? '4 5' : dash}
    />
  );

  return (
    <svg
      viewBox="0 0 28 28"
      className={`halo${glow ? ' glowing' : ''}${className ? ` ${className}` : ''}`}
      style={{ color }}
      aria-hidden="true"
    >
      {/* Broken: gapped on purpose. The one rank you fall into should look like
          something came apart rather than merely being a duller colour. */}
      {style === 'broken' && ring(9, 1.2, '4 5')}
      {style === 'thin' && ring(9, 1)}
      {style === 'solid' && ring(9, 1.8)}
      {style === 'double' && (
        <>
          {ring(10, 1.4)}
          {ring(7, 1.4)}
        </>
      )}
      {style === 'radiant' && (
        <>
          {ring(9, 1.8)}
          {/* Twelve rays, short and even. Light coming off it, not a sun. */}
          {Array.from({ length: 12 }, (_, i) => {
            const angle = (i / 12) * Math.PI * 2;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            return (
              <line
                key={i}
                x1={C + cos * 11.2}
                y1={C + sin * 11.2}
                x2={C + cos * 13.4}
                y2={C + sin * 13.4}
                stroke={color}
                strokeWidth={1.2}
                strokeLinecap="round"
              />
            );
          })}
        </>
      )}
    </svg>
  );
}
